/* eslint-disable import/no-duplicates */
import { DatePicker } from "@mui/lab";
import PickersDay, { PickersDayProps } from "@mui/lab/PickersDay";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import endOfWeek from "date-fns/endOfWeek";
import isSameDay from "date-fns/isSameDay";
import isWithinInterval from "date-fns/isWithinInterval";
import startOfWeek from "date-fns/startOfWeek";
import * as React from "react";
import { useTranslation } from "react-i18next";

type CustomPickerDayProps = PickersDayProps<Date> & {
  dayIsBetween: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
};

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "dayIsBetween" && prop !== "isFirstDay" && prop !== "isLastDay",
})<CustomPickerDayProps>(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
  }),
  ...(isLastDay && {
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  }),
})) as React.ComponentType<CustomPickerDayProps>;

type Props = {
  onDateChange: (val: Date | null) => void;
  date: Date;
};

export const WeekDatePicker = ({ onDateChange, date }: Props): JSX.Element => {
  const { t } = useTranslation("week-date-picker");
  const [open, setOpen] = React.useState<boolean>(false);

  const renderWeekPickerDay = (
    _date: Date,
    _selectedDates: Array<Date | null>,
    pickersDayProps: PickersDayProps<Date>,
  ) => {
    if (!date) {
      return <PickersDay {...pickersDayProps} />;
    }

    const start = startOfWeek(date, { weekStartsOn: 1 });
    const end = endOfWeek(date, { weekStartsOn: 1 });

    const dayIsBetween = isWithinInterval(_date, { start, end });
    const isFirstDay = isSameDay(_date, start);
    const isLastDay = isSameDay(_date, end);

    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

  return (
    <DatePicker
      label={t("berths:week-picker")}
      value={date}
      onChange={(newValue) => {
        onDateChange(newValue);
      }}
      open={open}
      renderDay={renderWeekPickerDay}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            inputProps={{ ...params.inputProps, onChange: undefined }}
            onClick={() => setOpen(!open)}
          />
        );
      }}
      onClose={() => setOpen(false)}
      inputFormat={t("week-format")}
      mask={t("mask")}
    />
  );
};
